import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context';
import api from '../api/client';
import Turnstile, { TURNSTILE_SITE_KEY } from '../components/Turnstile';

// ── Badge images (1–28) ───────────────────────────────────────────────────────
const BADGE_IMGS = {};
for (let i = 1; i <= 28; i++) {
  BADGE_IMGS[i] = require(`../assets/badges/Badges_${i}.webp`); // eslint-disable-line
}

// ── Constants ─────────────────────────────────────────────────────────────────
const EXP_ORDER   = ['beginner', 'intermediate', 'expert', 'veteran'];
const EXP_COLOR   = { beginner: '#44aa66', intermediate: '#4488cc', expert: '#aa7bcc', veteran: '#f0c040' };
const ROLE_ICON   = { player: '🎲', gm: '📖', both: '⚔️' };
const ROLE_COLOR  = { player: '#4488cc', gm: '#cc4444', both: '#9966cc' };
const AVAIL_OPTS  = ['online', 'in_person', 'weekdays', 'weekends', 'flexible'];
const LOOK_OPTS   = ['group', 'players', 'campaign', 'oneshot', 'convention'];
const LANG_OPTS   = ['lt', 'en', 'ru', 'de', 'pl', 'fr', 'lv', 'et'];
const COUNTRY_FLAG = {
  LT:'🇱🇹', UK:'🇬🇧', DE:'🇩🇪', PL:'🇵🇱', FR:'🇫🇷', ES:'🇪🇸',
  LV:'🇱🇻', EE:'🇪🇪', FI:'🇫🇮', SE:'🇸🇪', NO:'🇳🇴', DK:'🇩🇰',
  NL:'🇳🇱', BE:'🇧🇪', CH:'🇨🇭', AT:'🇦🇹', CZ:'🇨🇿', SK:'🇸🇰',
  HU:'🇭🇺', RO:'🇷🇴', HR:'🇭🇷', SI:'🇸🇮', PT:'🇵🇹', IT:'🇮🇹',
  UA:'🇺🇦', RU:'🇷🇺', US:'🇺🇸', CA:'🇨🇦', AU:'🇦🇺', JP:'🇯🇵',
};

const COUNTRY_OPTS = [
  { code: 'LT', name: 'Lietuva' }, { code: 'LV', name: 'Latvija' }, { code: 'EE', name: 'Estija' },
  { code: 'PL', name: 'Lenkija' }, { code: 'DE', name: 'Vokietija' }, { code: 'UK', name: 'Jungtinė Karalystė' },
  { code: 'FR', name: 'Prancūzija' }, { code: 'ES', name: 'Ispanija' }, { code: 'IT', name: 'Italija' },
  { code: 'NL', name: 'Nyderlandai' }, { code: 'BE', name: 'Belgija' }, { code: 'CH', name: 'Šveicarija' },
  { code: 'AT', name: 'Austrija' }, { code: 'CZ', name: 'Čekija' }, { code: 'SK', name: 'Slovakija' },
  { code: 'HU', name: 'Vengrija' }, { code: 'FI', name: 'Suomija' }, { code: 'SE', name: 'Švedija' },
  { code: 'DK', name: 'Danija' }, { code: 'NO', name: 'Norvegija' }, { code: 'RU', name: 'Rusija' },
  { code: 'UA', name: 'Ukraina' }, { code: 'US', name: 'JAV' }, { code: 'CA', name: 'Kanada' },
  { code: 'AU', name: 'Australija' }, { code: 'JP', name: 'Japonija' },
];

const POPULAR_SYSTEMS = [
  'D&D 5E', 'Pathfinder 2E', 'Old-School Essentials', 'Call of Cthulhu',
  'World of Darkness', 'Alien RPG', 'Mothership', 'MÖRK BORG',
  'Starfinder', 'Cyberpunk RED', 'Shadowrun', 'LANCER',
  'Vaesen', 'Blades in the Dark', 'Marvel Multiverse', 'Dragonbane',
];

const EMPTY_PROFILE = {
  avatar: '', header_image: '', bio: '', city: '', country: '', experience: 'beginner', role: 'player',
  systems: [], availability: [], looking_for: [], languages: [], discord: '', website: '', public: 1,
};

function ImageDropUpload({ value, onChange, label, aspectRatio = '1/1', placeholder = '📷' }) {
  const [dragging, setDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);

  const upload = async file => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await api.post('/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      onChange(r.data.url);
    } catch { /* silent */ }
    setUploading(false);
  };

  return (
    <div>
      {label && <div style={{ fontSize: 11, color: 'rgba(200,180,255,0.6)', letterSpacing: 1, marginBottom: 6, textTransform: 'uppercase' }}>{label}</div>}
      <div
        onClick={() => inputRef.current?.click()}
        onDragOver={e => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={e => { e.preventDefault(); setDragging(false); upload(e.dataTransfer.files[0]); }}
        style={{
          position: 'relative', aspectRatio, borderRadius: aspectRatio === '1/1' ? '50%' : 10,
          width: aspectRatio === '1/1' ? 96 : '100%', maxWidth: '100%',
          background: dragging ? 'rgba(240,192,64,0.1)' : '#1a1428',
          border: `2px dashed ${dragging ? '#f0c040' : 'rgba(200,180,255,0.25)'}`,
          cursor: 'pointer', overflow: 'hidden', transition: 'all .2s',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}
      >
        {value
          ? <img src={value} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
          : <span style={{ fontSize: aspectRatio === '1/1' ? 28 : 22, opacity: 0.4 }}>{placeholder}</span>
        }
        {uploading && (
          <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, color: '#f0c040' }}>⏳</div>
        )}
        <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0)', display: 'flex', alignItems: 'flex-end', justifyContent: 'center', opacity: 0, transition: 'all .2s', fontSize: 11, color: '#fff', paddingBottom: 6, '&:hover': { opacity: 1 } }} className="img-upload-hover">
        </div>
      </div>
      {value && (
        <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
          <input
            style={{ flex: 1, background: '#1a1428', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 6, color: '#fff', padding: '5px 10px', fontSize: 11, outline: 'none' }}
            value={value} onChange={e => onChange(e.target.value)} placeholder="https://..." />
          <button onClick={() => onChange('')} style={{ background: 'rgba(204,68,68,0.15)', border: '1px solid rgba(204,68,68,0.3)', borderRadius: 6, color: '#cc4444', padding: '5px 10px', cursor: 'pointer', fontSize: 11 }}>✕</button>
        </div>
      )}
      <input ref={inputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={e => upload(e.target.files[0])} />
    </div>
  );
}

function getInitials(name) {
  return (name || '?').split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase();
}

function avatarBg(userId) {
  const colors = ['#3d1f7c', '#1f3d7c', '#1f5c3d', '#7c3d1f', '#7c1f3d', '#3d5c1f'];
  return colors[(userId || 0) % colors.length];
}

// ── Avatar component ──────────────────────────────────────────────────────────
function Avatar({ player, size = 56 }) {
  if (player.avatar) {
    return (
      <img
        src={player.avatar} alt={player.name}
        style={{ width: size, height: size, borderRadius: '50%', objectFit: 'cover', border: '2px solid rgba(240,192,64,0.35)', flexShrink: 0 }}
      />
    );
  }
  return (
    <div style={{
      width: size, height: size, borderRadius: '50%', background: avatarBg(player.user_id),
      display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
      fontFamily: 'Cinzel,serif', fontSize: size * 0.32, fontWeight: 700, color: '#f0c040',
      border: '2px solid rgba(240,192,64,0.3)',
    }}>
      {getInitials(player.name)}
    </div>
  );
}

// ── RoleBadge ─────────────────────────────────────────────────────────────────
function RoleBadge({ role, t }) {
  const color = ROLE_COLOR[role] || '#777';
  return (
    <span className="pl-badge" style={{ background: color + '22', border: `1px solid ${color}55`, color }}>
      {ROLE_ICON[role]} {t(`players.role_${role}`)}
    </span>
  );
}

// ── ExpBadge ──────────────────────────────────────────────────────────────────
function ExpBadge({ exp, t }) {
  const color = EXP_COLOR[exp] || '#777';
  return (
    <span className="pl-badge" style={{ background: color + '22', border: `1px solid ${color}55`, color }}>
      {t(`players.exp_${exp}`)}
    </span>
  );
}

const PAGE_SIZE = 18;

// ── Contact Modal ─────────────────────────────────────────────────────────────
function ContactModal({ player, onClose, user, t }) {
  const [msg, setMsg]       = useState('');
  const [sending, setSending] = useState(false);
  const [sent, setSent]     = useState(false);
  const [error, setError]   = useState('');
  const [captcha, setCaptcha] = useState('');
  const captchaResetRef = useRef(null);
  const captchaOk = !TURNSTILE_SITE_KEY || !!captcha;

  const send = async () => {
    if (!msg.trim() || !captchaOk) return;
    setSending(true); setError('');
    try {
      await api.post(`/players/${player.user_id}/contact`, { message: msg.trim(), turnstile_token: captcha });
      setSent(true);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Klaida siunčiant žinutę');
      captchaResetRef.current?.();
    } finally {
      setSending(false);
    }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(0,0,0,0.85)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}
      onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={{ background: '#0d0820', border: '1px solid rgba(240,192,64,0.2)', borderRadius: 16, maxWidth: 440, width: '100%', overflow: 'hidden' }}>
        <div style={{ background: 'linear-gradient(135deg,#1a0f3c,#2d1a5c)', padding: '18px 22px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <Avatar player={player} size={36} />
            <div>
              <div style={{ fontFamily: 'Cinzel,serif', fontSize: 13, color: '#f0c040', fontWeight: 700 }}>Susisiekti</div>
              <div style={{ fontSize: 11, color: 'rgba(200,180,255,0.6)' }}>{player.name}</div>
            </div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.5)', fontSize: 22, cursor: 'pointer' }}>×</button>
        </div>

        <div style={{ padding: '20px 22px' }}>
          {!user ? (
            <div style={{ textAlign: 'center', padding: '20px 0', color: 'rgba(200,180,255,0.6)', fontSize: 13 }}>
              <div style={{ fontSize: 36, marginBottom: 10 }}>🔐</div>
              Prisijunkite, norėdami susisiekti su žaidėjais
            </div>
          ) : sent ? (
            <div style={{ textAlign: 'center', padding: '20px 0' }}>
              <div style={{ fontSize: 36, marginBottom: 10 }}>✅</div>
              <div style={{ color: '#50e080', fontSize: 14 }}>Žinutė išsiųsta!</div>
            </div>
          ) : (
            <>
              {error && <div style={{ background: 'rgba(240,60,60,0.1)', border: '1px solid rgba(240,60,60,0.3)', borderRadius: 7, color: '#ff7070', padding: '8px 12px', fontSize: 12, marginBottom: 12 }}>⚠ {error}</div>}
              <label style={{ display: 'block', fontSize: 11, color: 'rgba(200,180,255,0.5)', letterSpacing: 1, textTransform: 'uppercase', marginBottom: 6 }}>
                Žinutė
              </label>
              <textarea
                value={msg}
                onChange={e => setMsg(e.target.value)}
                rows={5}
                maxLength={1000}
                placeholder={`Sveiki, ${player.name}! Norėčiau...`}
                autoFocus
                style={{ width: '100%', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8, color: '#fff', padding: '10px 12px', fontSize: 13, resize: 'vertical', outline: 'none', boxSizing: 'border-box' }}
              />
              <div style={{ fontSize: 11, color: 'rgba(200,180,255,0.3)', textAlign: 'right', marginTop: 4 }}>{msg.length}/1000</div>
              <Turnstile onToken={setCaptcha} resetRef={captchaResetRef} />
            </>
          )}
        </div>

        {user && !sent && (
          <div style={{ padding: '0 22px 20px', display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
            <button onClick={onClose} style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8, color: 'rgba(200,180,255,0.7)', padding: '9px 18px', cursor: 'pointer', fontSize: 13 }}>
              Atšaukti
            </button>
            <button onClick={send} disabled={sending || !msg.trim() || !captchaOk} style={{ background: 'linear-gradient(135deg,#5a3e8c,#7b5cbf)', border: 'none', borderRadius: 8, color: '#fff', padding: '9px 20px', cursor: 'pointer', fontSize: 13, fontWeight: 700, opacity: sending || !msg.trim() || !captchaOk ? 0.5 : 1 }}>
              {sending ? '⏳ Siunčiama...' : '✉️ Siųsti'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Pagination ────────────────────────────────────────────────────────────────
function Pagination({ page, total, pageSize, onPage }) {
  const pages = Math.ceil(total / pageSize);
  if (pages <= 1) return null;

  const btn = (p, label, disabled = false) => (
    <button
      key={p + label}
      onClick={() => !disabled && onPage(p)}
      disabled={disabled}
      style={{
        minWidth: 36, height: 36, padding: '0 10px',
        background: p === page ? 'linear-gradient(135deg,#5a3e8c,#7b5cbf)' : 'rgba(255,255,255,0.04)',
        border: p === page ? 'none' : '1px solid rgba(255,255,255,0.1)',
        borderRadius: 7, color: p === page ? '#fff' : 'rgba(200,180,255,0.7)',
        cursor: disabled ? 'default' : 'pointer', fontSize: 13, fontWeight: p === page ? 700 : 400,
        opacity: disabled ? 0.3 : 1, transition: 'all .15s',
      }}
    >
      {label}
    </button>
  );

  const nums = [];
  for (let i = 1; i <= pages; i++) {
    if (i === 1 || i === pages || (i >= page - 2 && i <= page + 2)) nums.push(i);
    else if (nums[nums.length - 1] !== '…') nums.push('…');
  }

  return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 6, marginTop: 36, paddingBottom: 20 }}>
      {btn(page - 1, '←', page === 1)}
      {nums.map((n, i) =>
        n === '…'
          ? <span key={'e' + i} style={{ color: 'rgba(200,180,255,0.3)', padding: '0 4px' }}>…</span>
          : btn(n, n, false)
      )}
      {btn(page + 1, '→', page === pages)}
    </div>
  );
}

// ── Player Card ───────────────────────────────────────────────────────────────
function PlayerCard({ player, isOwn, isAdmin, onEdit, onContact, user, t }) {
  const navigate = useNavigate();
  return (
    <div className="pl-card" onClick={() => navigate(`/players/${player.user_id}`)}>
      <div className="pl-card-header">
        <Avatar player={player} size={54} />
        <div className="pl-card-info">
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6, flexWrap: 'wrap' }}>
            <span className="pl-card-name">{player.name}</span>
            {isOwn && <span className="pl-you">YOU</span>}
          </div>
          <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
            <RoleBadge role={player.role} t={t} />
            <ExpBadge exp={player.experience} t={t} />
          </div>
          {(player.city || player.country) && (
            <div className="pl-card-meta">
              {player.country && COUNTRY_FLAG[player.country] && <span style={{ marginRight: 4 }}>{COUNTRY_FLAG[player.country]}</span>}
              {player.city && <span>📍 {player.city}</span>}
            </div>
          )}
        </div>
        <div className="pl-card-actions">
          {!isOwn && (
            <button className="pl-card-btn pl-card-btn-contact" onClick={e => { e.stopPropagation(); onContact(player); }} title="Susisiekti">✉️</button>
          )}
          {(isOwn || isAdmin) && (
            <button
              className={`pl-card-btn ${isOwn ? 'pl-card-btn-edit-own' : 'pl-card-btn-edit-admin'}`}
              onClick={e => { e.stopPropagation(); onEdit(); }}
              title={isOwn ? 'Redaguoti savo profilį' : 'Redaguoti žaidėją'}
            >✏️</button>
          )}
        </div>
      </div>

      <div className="pl-card-body">
        {player.bio && <div className="pl-card-bio">{player.bio}</div>}

        {player.systems && player.systems.length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 8 }}>
            {player.systems.slice(0, 4).map((s, i) => (
              <span key={i} className="pl-tag pl-tag-sys">{s}</span>
            ))}
            {player.systems.length > 4 && (
              <span className="pl-tag pl-tag-more">+{player.systems.length - 4}</span>
            )}
          </div>
        )}

        {player.availability && player.availability.length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
            {player.availability.map((a, i) => (
              <span key={i} className="pl-tag pl-tag-avail">{t(`players.avail_${a}`)}</span>
            ))}
          </div>
        )}

        {player.club_count > 0 && (
          <div className="pl-club-count">🛡️ {t('players.memberships_label')}: {player.club_count}</div>
        )}
      </div>
    </div>
  );
}

// ── Player Detail Modal ───────────────────────────────────────────────────────
function PlayerDetailModal({ player, onClose, t }) {
  const joined = player.joined ? new Date(player.joined).toLocaleDateString('lt-LT', { year: 'numeric', month: 'long' }) : null;
  const [memberships, setMemberships] = useState([]);
  useEffect(() => {
    api.get(`/players/${player.user_id}/memberships`).then(r => setMemberships(r.data || [])).catch(() => {});
  }, [player.user_id]); // eslint-disable-line

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 900, background: 'rgba(0,0,0,0.8)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}
      onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={{ background: '#0b0817', border: '1px solid rgba(240,192,64,0.2)', borderRadius: 18, maxWidth: 640, width: '100%', maxHeight: '88vh', overflowY: 'auto' }}>

        {/* Hero banner */}
        <div style={{ background: 'linear-gradient(135deg,#1a0f3c,#2d1a5c,#1a0f3c)', padding: '28px 28px 20px', position: 'relative' }}>
          <button onClick={onClose} style={{ position: 'absolute', top: 16, right: 18, background: 'rgba(255,255,255,0.08)', border: 'none', color: 'rgba(255,255,255,0.6)', fontSize: 22, cursor: 'pointer', borderRadius: '50%', width: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>×</button>

          <div style={{ display: 'flex', gap: 18, alignItems: 'flex-start' }}>
            <Avatar player={player} size={80} />
            <div>
              <h2 style={{ fontFamily: 'Cinzel,serif', fontSize: 22, color: '#f0c040', margin: '0 0 8px', fontWeight: 700 }}>{player.name}</h2>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 8 }}>
                <RoleBadge role={player.role} t={t} />
                <ExpBadge exp={player.experience} t={t} />
              </div>
              <div style={{ fontSize: 12, color: 'rgba(200,180,255,0.6)', display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                {(player.city || player.country) && (
                  <span>
                    {player.country && COUNTRY_FLAG[player.country] && <span style={{ marginRight: 4 }}>{COUNTRY_FLAG[player.country]}</span>}
                    {player.city && <>📍 {player.city}</>}
                  </span>
                )}
                {joined && <span>📅 {t('players.join_date')}: {joined}</span>}
              </div>
            </div>
          </div>
        </div>

        <div style={{ padding: '20px 28px', display: 'flex', flexDirection: 'column', gap: 18 }}>

          {/* Bio */}
          {player.bio && (
            <Section label={t('players.bio_label')}>
              <p style={{ fontSize: 14, color: 'rgba(200,180,255,0.85)', lineHeight: 1.7, margin: 0 }}>{player.bio}</p>
            </Section>
          )}

          {/* Systems */}
          {player.systems && player.systems.length > 0 && (
            <Section label={t('players.systems_label')}>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {player.systems.map((s, i) => (
                  <span key={i} style={{ fontSize: 12, fontWeight: 600, padding: '4px 10px', borderRadius: 6, background: 'rgba(90,62,140,0.3)', border: '1px solid rgba(90,62,140,0.5)', color: '#c8b4ff' }}>{s}</span>
                ))}
              </div>
            </Section>
          )}

          {/* Looking for */}
          {player.looking_for && player.looking_for.length > 0 && (
            <Section label={t('players.looking_for_label')}>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {player.looking_for.map((lf, i) => (
                  <span key={i} style={{ fontSize: 12, fontWeight: 600, padding: '4px 10px', borderRadius: 6, background: 'rgba(240,192,64,0.12)', border: '1px solid rgba(240,192,64,0.3)', color: '#f0c040' }}>{t(`players.looking_${lf}`)}</span>
                ))}
              </div>
            </Section>
          )}

          {/* Availability */}
          {player.availability && player.availability.length > 0 && (
            <Section label={t('players.availability_label')}>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {player.availability.map((a, i) => (
                  <span key={i} style={{ fontSize: 12, fontWeight: 600, padding: '4px 10px', borderRadius: 6, background: 'rgba(68,170,102,0.15)', border: '1px solid rgba(68,170,102,0.4)', color: '#6ddb8a' }}>{t(`players.avail_${a}`)}</span>
                ))}
              </div>
            </Section>
          )}

          {/* Languages */}
          {player.languages && player.languages.length > 0 && (
            <Section label={t('players.languages_label')}>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {player.languages.map((l, i) => (
                  <span key={i} style={{ fontSize: 12, fontWeight: 600, padding: '4px 10px', borderRadius: 6, background: 'rgba(80,120,200,0.15)', border: '1px solid rgba(80,120,200,0.35)', color: '#80a8ff' }}>{l.toUpperCase()}</span>
                ))}
              </div>
            </Section>
          )}

          {/* Contact */}
          {(player.discord || player.website) && (
            <Section label={t('players.contact_label')}>
              <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                {player.discord && (
                  <span style={{ fontSize: 13, color: 'rgba(200,180,255,0.8)', display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span style={{ fontSize: 16 }}>💬</span> {player.discord}
                  </span>
                )}
                {player.website && (
                  <a href={player.website} target="_blank" rel="noreferrer" style={{ fontSize: 13, color: '#f0c040', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 6 }}>
                    🔗 {player.website.replace(/^https?:\/\//, '').split('/')[0]}
                  </a>
                )}
              </div>
            </Section>
          )}

          {/* Club memberships */}
          {memberships.length > 0 && (
            <Section label={t('players.memberships_label')}>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {memberships.map(c => (
                  <span key={c.id} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12, fontWeight: 600, padding: '4px 10px', borderRadius: 6, background: 'rgba(240,192,64,0.1)', border: '1px solid rgba(240,192,64,0.3)', color: '#f0c040' }}>
                    {c.emoji && <span>{c.emoji}</span>}
                    {c.name}
                    {c.type && <span style={{ fontSize: 10, color: 'rgba(240,192,64,0.55)', textTransform: 'uppercase' }}>{c.type}</span>}
                  </span>
                ))}
              </div>
            </Section>
          )}
        </div>
      </div>
    </div>
  );
}

function Section({ label, children }) {
  return (
    <div>
      <div style={{ fontSize: 10, color: 'rgba(240,192,64,0.55)', letterSpacing: 2, textTransform: 'uppercase', marginBottom: 8, fontWeight: 600 }}>{label}</div>
      {children}
    </div>
  );
}

function InfoCard({ label, children, wide }) {
  return (
    <div className={`pl-info-card${wide ? ' pl-info-card-wide' : ''}`}>
      <div className="pl-section-lbl">{label}</div>
      {children}
    </div>
  );
}

// ── Edit Profile Modal ────────────────────────────────────────────────────────
function EditProfileModal({ initial, targetName, onSave, onClose, t }) {
  const [form, setForm] = useState({ ...EMPTY_PROFILE, ...initial });
  const [saving, setSaving] = useState(false);
  const [sysInput, setSysInput] = useState('');

  const setF = k => v => setForm(f => ({ ...f, [k]: v }));

  const toggleArr = (key, val) =>
    setForm(f => ({
      ...f,
      [key]: f[key].includes(val) ? f[key].filter(x => x !== val) : [...f[key], val],
    }));

  const addSystem = () => {
    const v = sysInput.trim();
    if (v && !form.systems.includes(v)) setForm(f => ({ ...f, systems: [...f.systems, v] }));
    setSysInput('');
  };
  const removeSystem = i => setForm(f => ({ ...f, systems: f.systems.filter((_, idx) => idx !== i) }));

  const save = async () => {
    setSaving(true);
    try {
      await onSave({
        ...form,
        systems: JSON.stringify(form.systems),
        availability: JSON.stringify(form.availability),
        looking_for: JSON.stringify(form.looking_for),
        languages: JSON.stringify(form.languages),
      });
      onClose();
    } finally {
      setSaving(false);
    }
  };

  const inp = 'pl-form-inp';
  const lbl = 'pl-form-lbl';

  const CheckChip = ({ label: chipLabel, checked, onClick }) => (
    <button onClick={onClick} className={`pl-check-chip ${checked ? 'pl-check-chip-on' : 'pl-check-chip-off'}`}>
      {checked ? '✓ ' : ''}{chipLabel}
    </button>
  );

  return (
    <div className="pl-modal-backdrop" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="pl-edit-modal">
        <div className="pl-edit-modal-header">
          <span className="pl-edit-modal-title">✏️ {targetName ? `Redaguoti: ${targetName}` : t('players.edit_profile')}</span>
          <button className="pl-edit-modal-close" onClick={onClose}>×</button>
        </div>

        <div className="pl-edit-modal-body">
          {/* Header background image */}
          <ImageDropUpload
            label="Antraštės fonas"
            value={form.header_image}
            onChange={setF('header_image')}
            aspectRatio="4/1"
            placeholder="🖼️ Vilkite arba spustelėkite – antraštės fonas"
          />

          {/* Avatar + basic info */}
          <div style={{ display: 'flex', gap: 18, alignItems: 'flex-start', flexWrap: 'wrap' }}>
            <ImageDropUpload
              label={t('players.field_avatar')}
              value={form.avatar}
              onChange={setF('avatar')}
              aspectRatio="1/1"
              placeholder="👤"
            />
            <div style={{ flex: 1, minWidth: 200, display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div>
                <label className={lbl}>{t('players.field_city')}</label>
                <input className={inp} value={form.city} onChange={e => setF('city')(e.target.value)} placeholder="Vilnius" />
              </div>
              <div>
                <label className={lbl}>Šalis</label>
                <select className={inp} style={{ cursor: 'pointer' }} value={form.country} onChange={e => setF('country')(e.target.value)}>
                  <option value="">— Pasirinkti —</option>
                  {COUNTRY_OPTS.map(c => (
                    <option key={c.code} value={c.code}>{COUNTRY_FLAG[c.code] || ''} {c.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className={lbl}>{t('players.field_discord')}</label>
                <input className={inp} value={form.discord} onChange={e => setF('discord')(e.target.value)} placeholder="username#1234" />
              </div>
              <div>
                <label className={lbl}>{t('players.field_website')}</label>
                <input className={inp} value={form.website} onChange={e => setF('website')(e.target.value)} placeholder="https://..." />
              </div>
            </div>
          </div>

          {/* Bio */}
          <div>
            <label className={lbl}>{t('players.field_bio')}</label>
            <textarea className={`${inp} pl-form-textarea`} value={form.bio} onChange={e => setF('bio')(e.target.value)} placeholder={t('players.bio_placeholder')} />
          </div>

          {/* Role */}
          <div>
            <label className={lbl}>{t('players.filter_role')}</label>
            <div style={{ display: 'flex', gap: 8 }}>
              {['player', 'gm', 'both'].map(r => (
                <CheckChip key={r} label={`${ROLE_ICON[r]} ${t(`players.role_${r}`)}`} checked={form.role === r} onClick={() => setF('role')(r)} />
              ))}
            </div>
          </div>

          {/* Experience */}
          <div>
            <label className={lbl}>{t('players.filter_exp')}</label>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {EXP_ORDER.map(e => (
                <CheckChip key={e} label={t(`players.exp_${e}`)} checked={form.experience === e} onClick={() => setF('experience')(e)} />
              ))}
            </div>
          </div>

          {/* Systems */}
          <div>
            <label className={lbl}>{t('players.systems_label')}</label>
            {/* Popular presets */}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginBottom: 10 }}>
              {POPULAR_SYSTEMS.map(s => (
                <button key={s} onClick={() => { if (!form.systems.includes(s)) setForm(f => ({ ...f, systems: [...f.systems, s] })); }}
                  className={`pl-check-chip ${form.systems.includes(s) ? 'pl-sys-preset-on' : 'pl-sys-preset-off'}`} style={{ fontSize: 11, padding: '3px 9px', borderRadius: 5 }}>
                  {s}
                </button>
              ))}
            </div>
            {/* Custom input */}
            <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
              <input className={inp} style={{ flex: 1 }} value={sysInput} onChange={e => setSysInput(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), addSystem())}
                placeholder={t('players.systems_ph')} />
              <button onClick={addSystem} className="btn btn-outline" style={{ color: '#f0c040', borderColor: 'rgba(240,192,64,0.3)', fontSize: 12, whiteSpace: 'nowrap' }}>+ {t('players.add_system')}</button>
            </div>
            {form.systems.length > 0 && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
                {form.systems.map((s, i) => (
                  <span key={i} className="pl-tag pl-tag-sys" style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11, padding: '3px 9px', borderRadius: 5 }}>
                    {s}
                    <button onClick={() => removeSystem(i)} style={{ background: 'none', border: 'none', color: '#f0c040', cursor: 'pointer', padding: 0, fontSize: 13, lineHeight: 1 }}>×</button>
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Availability */}
          <div>
            <label className={lbl}>{t('players.availability_label')}</label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {AVAIL_OPTS.map(a => (
                <CheckChip key={a} label={t(`players.avail_${a}`)} checked={form.availability.includes(a)} onClick={() => toggleArr('availability', a)} />
              ))}
            </div>
          </div>

          {/* Looking for */}
          <div>
            <label className={lbl}>{t('players.looking_for_label')}</label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {LOOK_OPTS.map(l => (
                <CheckChip key={l} label={t(`players.looking_${l}`)} checked={form.looking_for.includes(l)} onClick={() => toggleArr('looking_for', l)} />
              ))}
            </div>
          </div>

          {/* Languages */}
          <div>
            <label className={lbl}>{t('players.languages_label')}</label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {LANG_OPTS.map(l => (
                <CheckChip key={l} label={l.toUpperCase()} checked={form.languages.includes(l)} onClick={() => toggleArr('languages', l)} />
              ))}
            </div>
          </div>

          {/* Public toggle */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <button
              onClick={() => setF('public')(form.public ? 0 : 1)}
              style={{ width: 44, height: 24, borderRadius: 12, border: 'none', cursor: 'pointer', background: form.public ? '#44aa66' : 'rgba(255,255,255,0.1)', transition: 'all .2s', position: 'relative', flexShrink: 0 }}
            >
              <span style={{ position: 'absolute', top: 2, left: form.public ? 22 : 2, width: 20, height: 20, borderRadius: '50%', background: '#fff', transition: 'all .2s' }} />
            </button>
            <span style={{ fontSize: 13, color: 'rgba(200,180,255,0.8)' }}>{t('players.public_label')}</span>
          </div>
        </div>

        <div className="pl-edit-modal-footer">
          <button className="pl-form-btn-cancel" onClick={onClose}>{t('players.cancel')}</button>
          <button className="pl-form-btn-save" onClick={save} disabled={saving}>
            {saving ? t('players.saving') : `💾 ${t('players.save')}`}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Filter Bar ────────────────────────────────────────────────────────────────
function FilterBar({ search, onSearch, roleFilter, onRole, expFilter, onExp, t }) {
  return (
    <div className="pl-filter">
      <input className="pl-filter-search" value={search} onChange={e => onSearch(e.target.value)} placeholder={`🔍  ${t('players.search_ph')}`} />
      <div className="pl-filter-group">
        <button className={`pl-filter-btn${!roleFilter ? ' active' : ''}`} onClick={() => onRole('')}>{t('players.all')}</button>
        {['player', 'gm', 'both'].map(r => (
          <button key={r} className={`pl-filter-btn${roleFilter === r ? ' active' : ''}`} onClick={() => onRole(r)}>
            {ROLE_ICON[r]} {t(`players.role_${r}`)}
          </button>
        ))}
      </div>
      <div className="pl-filter-group">
        <button className={`pl-filter-btn${!expFilter ? ' active' : ''}`} onClick={() => onExp('')}>{t('players.all')}</button>
        {EXP_ORDER.map(e => (
          <button key={e} className={`pl-filter-btn${expFilter === e ? ' active' : ''}`} onClick={() => onExp(e)}>
            {t(`players.exp_${e}`)}
          </button>
        ))}
      </div>
    </div>
  );
}

// ── Admin Player Picker Modal ─────────────────────────────────────────────────
function PlayerPickerModal({ onPick, onClose, t }) {
  const [all, setAll]       = useState([]);
  const [q,   setQ]         = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/admin/users?limit=200')
      .then(r => setAll((r.data.users || []).map(u => ({ user_id: u.id, name: u.name, email: u.email, role: u.role }))))
      .catch(() => setAll([]))
      .finally(() => setLoading(false));
  }, []);

  const shown = q.trim()
    ? all.filter(p => p.name.toLowerCase().includes(q.toLowerCase()) || (p.email || '').toLowerCase().includes(q.toLowerCase()))
    : all;

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(0,0,0,0.85)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}
      onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={{ background: '#0d0a18', border: '1px solid rgba(240,192,64,0.25)', borderRadius: 18, maxWidth: 520, width: '100%', maxHeight: '80vh', display: 'flex', flexDirection: 'column' }}>
        <div style={{ padding: '18px 24px', borderBottom: '1px solid rgba(255,255,255,0.06)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
          <span style={{ fontFamily: 'Cinzel,serif', fontSize: 15, color: '#f0c040' }}>🎲 Pasirinkite žaidėją</span>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.5)', fontSize: 22, cursor: 'pointer' }}>×</button>
        </div>
        <div style={{ padding: '14px 24px 8px', flexShrink: 0 }}>
          <input
            autoFocus
            value={q}
            onChange={e => setQ(e.target.value)}
            placeholder="🔍  Ieškoti pagal vardą..."
            style={{ width: '100%', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8, color: '#fff', padding: '9px 14px', fontSize: 13, outline: 'none', boxSizing: 'border-box' }}
          />
        </div>
        <div style={{ overflowY: 'auto', padding: '8px 24px 20px' }}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: 32, color: '#f0c040', fontSize: 13 }}>Kraunama...</div>
          ) : shown.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 32, color: 'rgba(200,180,255,0.4)', fontSize: 13 }}>Nerasta</div>
          ) : shown.map(p => (
            <div
              key={p.user_id}
              onClick={() => onPick(p)}
              style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 12px', borderRadius: 10, cursor: 'pointer', transition: 'background .15s', marginBottom: 4 }}
              onMouseEnter={e => e.currentTarget.style.background = 'rgba(240,192,64,0.07)'}
              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
            >
              <Avatar player={p} size={38} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontFamily: 'Cinzel,serif', fontSize: 13, color: '#ede8f8', fontWeight: 600 }}>{p.name}</div>
                <div style={{ fontSize: 11, color: 'rgba(200,180,255,0.4)', marginTop: 2 }}>
                  {p.email && <span style={{ marginRight: 8 }}>{p.email}</span>}
                  {p.city && <span>📍 {p.city}</span>}
                </div>
              </div>
              <div style={{ display: 'flex', gap: 5 }}>
                <RoleBadge role={p.role} t={t} />
                <ExpBadge exp={p.experience} t={t} />
              </div>
              <span style={{ fontSize: 11, color: 'rgba(240,192,64,0.5)' }}>→</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ── Main PlayersPage ──────────────────────────────────────────────────────────
export default function PlayersPage() {
  const { t } = useTranslation();
  const { user, isAdmin } = useAuth();

  const [players, setPlayers]       = useState([]);
  const [myProfile, setMyProfile]   = useState(null);
  const [loading, setLoading]       = useState(true);
  const [editTarget, setEditTarget] = useState(null); // { userId, profile, name }
  const [pickerOpen, setPickerOpen] = useState(false);
  const [contactPlayer, setContact] = useState(null);
  const [search, setSearch]         = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [expFilter, setExpFilter]   = useState('');
  const [page, setPage]             = useState(1);

  const loadPlayers = useCallback(() => {
    setLoading(true);
    api.get('/players').then(r => setPlayers(r.data)).catch(() => setPlayers([])).finally(() => setLoading(false));
  }, []);

  const loadMyProfile = useCallback(() => {
    if (!user) { setMyProfile(null); return; }
    api.get('/players/me').then(r => setMyProfile(r.data)).catch(() => setMyProfile(null));
  }, [user]);

  useEffect(() => { loadPlayers(); }, [loadPlayers]);
  useEffect(() => { loadMyProfile(); }, [loadMyProfile]);

  const handleSave = async (payload) => {
    if (editTarget && editTarget.userId !== user?.id) {
      await api.put(`/admin/players/${editTarget.userId}`, payload);
    } else {
      await api.put('/players/me', payload);
      loadMyProfile();
    }
    loadPlayers();
    setEditTarget(null);
  };

  const openOwnEdit = () => {
    setEditTarget({ userId: user?.id, profile: myProfile, name: user?.name });
  };

  const openAdminEdit = async (player) => {
    setPickerOpen(false);
    try {
      const r = await api.get(`/admin/players/${player.user_id}`);
      const p = r.data;
      setEditTarget({
        userId: p.user_id,
        name: player.name,
        profile: {
          ...p,
          systems:      Array.isArray(p.systems)      ? p.systems      : [],
          availability: Array.isArray(p.availability) ? p.availability : [],
          looking_for:  Array.isArray(p.looking_for)  ? p.looking_for  : [],
          languages:    Array.isArray(p.languages)     ? p.languages    : [],
        },
      });
    } catch (err) {
      const status = err?.response?.status;
      if (status === 404) {
        setEditTarget({ userId: player.user_id, name: player.name, profile: { ...EMPTY_PROFILE } });
      } else {
        alert('Nepavyko įkelti profilio');
      }
    }
  };

  const filtered = useMemo(() => {
    const result = players.filter(p => {
      if (roleFilter && p.role !== roleFilter) return false;
      if (expFilter && p.experience !== expFilter) return false;
      if (search) {
        const q = search.toLowerCase();
        if (!p.name.toLowerCase().includes(q) &&
            !(p.city || '').toLowerCase().includes(q) &&
            !(p.bio || '').toLowerCase().includes(q) &&
            !((p.systems || []).some(s => s.toLowerCase().includes(q)))) return false;
      }
      return true;
    });
    return result;
  }, [players, roleFilter, expFilter, search]);

  // Reset page when filters change
  useEffect(() => { setPage(1); }, [search, roleFilter, expFilter]);

  const pageItems = useMemo(
    () => filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE),
    [filtered, page]
  );

  const myCard = myProfile && user ? { ...myProfile, name: user.name } : null;
  const isInList = players.some(p => p.user_id === user?.id);
  const hasOwnCard = myCard && myCard.public === 1 && isInList;

  return (
    <div className="pl-page">

      {/* Header */}
      <div className="pl-page-header">
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
            <span style={{ fontSize: 32 }}>🎲</span>
            <h1 className="pl-page-title">{t('players.title')}</h1>
          </div>
          <p className="pl-page-sub">{t('players.subtitle')} — {players.length} {t('players.count_label')}</p>
        </div>

        {user && (
          <div style={{ display: 'flex', gap: 8 }}>
            {isAdmin && (
              <button onClick={() => setPickerOpen(true)} className="btn btn-outline" style={{ color: '#f0c040', borderColor: 'rgba(240,192,64,0.3)', display: 'flex', alignItems: 'center', gap: 8 }}>
                🎲 Redaguoti žaidėją
              </button>
            )}
            <button onClick={openOwnEdit} className="btn btn-primary" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              ✏️ {t('players.edit_profile')}
            </button>
          </div>
        )}
      </div>

      {/* Filter bar */}
      <FilterBar search={search} onSearch={setSearch} roleFilter={roleFilter} onRole={setRoleFilter} expFilter={expFilter} onExp={setExpFilter} t={t} />

      {/* Own profile preview (if private or no card in list) */}
      {myCard && !hasOwnCard && (
        <div className="pl-private-banner">
          <span style={{ fontSize: 13, color: 'rgba(200,180,255,0.7)' }}>🔒 {t('players.profile_private')}</span>
          <button onClick={openOwnEdit} className="btn btn-outline" style={{ fontSize: 12, padding: '5px 14px', color: '#f0c040', borderColor: 'rgba(240,192,64,0.3)' }}>
            {t('players.make_public')}
          </button>
        </div>
      )}

      {/* Grid */}
      {loading ? (
        <div className="pl-empty"><div className="pl-empty-icon">⏳</div><div style={{ color: '#f0c040' }}>{t('players.loading')}</div></div>
      ) : filtered.length === 0 ? (
        <div className="pl-empty">
          <div className="pl-empty-icon">🎲</div>
          <div style={{ color: '#f0c040', fontSize: 15 }}>{search || roleFilter || expFilter ? t('players.no_results') : t('players.empty')}</div>
          {!user && <p style={{ color: 'rgba(200,180,255,0.5)', fontSize: 13, marginTop: 10 }}>{t('players.join_hint')}</p>}
        </div>
      ) : (
        <>
          <div className="pl-grid">
            {pageItems.map(p => (
              <PlayerCard
                key={p.user_id}
                player={p}
                isOwn={user && p.user_id === user.id}
                isAdmin={isAdmin}
                onEdit={user && p.user_id === user.id ? openOwnEdit : () => openAdminEdit(p)}
                onContact={setContact}
                user={user}
                t={t}
              />
            ))}
          </div>
          <Pagination page={page} total={filtered.length} pageSize={PAGE_SIZE} onPage={p => { setPage(p); window.scrollTo({ top: 0, behavior: 'smooth' }); }} />
        </>
      )}

      {/* Contact modal */}
      {contactPlayer && <ContactModal player={contactPlayer} onClose={() => setContact(null)} user={user} t={t} />}

      {/* Player picker (admin) */}
      {pickerOpen && (
        <PlayerPickerModal
          onPick={openAdminEdit}
          onClose={() => setPickerOpen(false)}
          t={t}
        />
      )}

      {/* Edit modal */}
      {editTarget && (
        <EditProfileModal
          initial={editTarget.profile || EMPTY_PROFILE}
          targetName={editTarget.userId !== user?.id ? editTarget.name : null}
          onSave={handleSave}
          onClose={() => setEditTarget(null)}
          t={t}
        />
      )}
    </div>
  );
}

// ── Mini Calendar ─────────────────────────────────────────────────────────────
function MiniCalendar({ events = [], navigate }) {
  const now = new Date();
  const [view, setView]       = useState({ year: now.getFullYear(), month: now.getMonth() });
  const [popup, setPopup]     = useState(null); // { day, evs }
  const today      = now.getDate();
  const isThisMonth = view.year === now.getFullYear() && view.month === now.getMonth();

  const firstDay    = new Date(view.year, view.month, 1).getDay();
  const offset      = (firstDay + 6) % 7; // Mon-start
  const daysInMonth = new Date(view.year, view.month + 1, 0).getDate();

  // Build map: "DD" → [event, ...]  for current view month
  const pad = n => String(n).padStart(2, '0');
  const prefix = `${view.year}-${pad(view.month + 1)}-`;
  const dayMap = {};
  for (const ev of events) {
    if (ev.date && ev.date.startsWith(prefix)) {
      const day = parseInt(ev.date.slice(8, 10), 10);
      if (!dayMap[day]) dayMap[day] = [];
      dayMap[day].push(ev);
    }
  }

  const cells = [];
  for (let i = 0; i < offset; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  const monthName = new Date(view.year, view.month, 1)
    .toLocaleDateString('lt-LT', { month: 'long', year: 'numeric' });
  const prev = () => { setPopup(null); setView(v => v.month === 0 ? { year: v.year - 1, month: 11 } : { year: v.year, month: v.month - 1 }); };
  const next = () => { setPopup(null); setView(v => v.month === 11 ? { year: v.year + 1, month: 0 } : { year: v.year, month: v.month + 1 }); };

  const handleDay = (d) => {
    if (!d || !dayMap[d]) return;
    const evs = dayMap[d];
    if (evs.length === 1) {
      navigate(`/events/${evs[0].id}`);
    } else {
      setPopup(popup?.day === d ? null : { day: d, evs });
    }
  };

  return (
    <div className="pl-cal" style={{ position: 'relative' }}>
      <div className="pl-cal-header">
        <button onClick={prev} style={{ background: 'none', border: 'none', color: 'rgba(200,180,255,0.5)', cursor: 'pointer', fontSize: 16, lineHeight: 1 }}>‹</button>
        <span style={{ textTransform: 'capitalize' }}>{monthName}</span>
        <button onClick={next} style={{ background: 'none', border: 'none', color: 'rgba(200,180,255,0.5)', cursor: 'pointer', fontSize: 16, lineHeight: 1 }}>›</button>
      </div>
      <div className="pl-cal-grid">
        {['P','A','T','K','P','Š','S'].map((dn, i) => <div key={i} className="pl-cal-day-name">{dn}</div>)}
        {cells.map((d, i) => {
          const hasEv  = d && dayMap[d];
          const isToday = d && isThisMonth && d === today;
          return (
            <div
              key={i}
              onClick={() => handleDay(d)}
              title={hasEv ? dayMap[d].map(e => e.name).join(', ') : undefined}
              style={{ position: 'relative', cursor: hasEv ? 'pointer' : 'default' }}
              className={`pl-cal-day${d === null ? ' pl-cal-day-other' : isToday ? ' pl-cal-day-today' : ''}${hasEv ? ' pl-cal-day-event' : ''}`}
            >
              {d ?? ''}
              {hasEv && (
                <span style={{
                  position: 'absolute', bottom: 1, left: '50%', transform: 'translateX(-50%)',
                  width: 4, height: 4, borderRadius: '50%',
                  background: isToday ? '#fff' : '#f0c040', display: 'block',
                }} />
              )}
            </div>
          );
        })}
      </div>

      {/* Multi-event popup */}
      {popup && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 20, marginTop: 6,
          background: '#1a1030', border: '1px solid rgba(240,192,64,0.3)', borderRadius: 10,
          padding: '10px 12px', boxShadow: '0 8px 24px rgba(0,0,0,0.5)',
        }}>
          <div style={{ fontSize: 10, color: 'rgba(240,192,64,0.55)', letterSpacing: 1, textTransform: 'uppercase', marginBottom: 8 }}>
            {pad(popup.day)}.{pad(view.month + 1)} renginiai
          </div>
          {popup.evs.map(ev => (
            <div
              key={ev.id}
              onClick={() => navigate(`/events/${ev.id}`)}
              style={{
                padding: '6px 8px', borderRadius: 7, cursor: 'pointer', fontSize: 12,
                color: 'rgba(200,180,255,0.85)', display: 'flex', alignItems: 'center', gap: 7,
                transition: 'background .12s',
              }}
              onMouseEnter={e => e.currentTarget.style.background = 'rgba(240,192,64,0.08)'}
              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
            >
              <span>{ev.type === 'larp' ? '🗡️' : '🎲'}</span>
              <span style={{ flex: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{ev.name}</span>
              {ev.city && <span style={{ fontSize: 10, color: 'rgba(200,180,255,0.35)' }}>📍{ev.city}</span>}
            </div>
          ))}
          <button onClick={() => setPopup(null)} style={{ background: 'none', border: 'none', color: 'rgba(200,180,255,0.3)', fontSize: 10, cursor: 'pointer', marginTop: 4 }}>✕ uždaryti</button>
        </div>
      )}
    </div>
  );
}

// ── Rating Widget ─────────────────────────────────────────────────────────────
function RatingWidget({ playerId, user, onContact, isOwn }) {
  const [rating, setRating] = useState(null);
  const [hover, setHover]   = useState(0);
  const [voting, setVoting] = useState(false);

  useEffect(() => {
    api.get(`/players/${playerId}/rating`).then(r => setRating(r.data)).catch(() => {});
  }, [playerId]);

  const vote = async (score) => {
    if (!user || isOwn || voting) return;
    setVoting(true);
    try {
      const r = await api.post(`/players/${playerId}/rate`, { score });
      setRating(prev => ({ ...prev, score: r.data.score, count: r.data.count, my_score: score }));
    } catch { /* silent */ }
    setVoting(false);
  };

  const trend = rating?.trend;
  const score = rating?.score || 0;
  const count = rating?.count || 0;
  const myScore = rating?.my_score || 0;

  return (
    <div style={{
      display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8,
      background: 'rgba(0,0,0,0.25)', borderRadius: 14, padding: '14px 18px',
      border: '1px solid rgba(240,192,64,0.15)', minWidth: 160,
    }}>
      {/* Score + trend */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{ fontFamily: 'Cinzel,serif', fontSize: 38, fontWeight: 700, color: '#f0c040', lineHeight: 1 }}>
          {score > 0 ? score.toFixed(1) : '—'}
        </span>
        {trend === 'up'   && <span style={{ color: '#44dd66', fontSize: 20 }} title="Kylantis">▲</span>}
        {trend === 'down' && <span style={{ color: '#ff5555', fontSize: 20 }} title="Krentantis">▼</span>}
        {trend === 'stable' && <span style={{ color: 'rgba(200,180,255,0.3)', fontSize: 16 }}>━</span>}
      </div>
      <div style={{ fontSize: 11, color: 'rgba(200,180,255,0.4)', marginTop: -6 }}>{count} {count === 1 ? 'balsas' : 'balsai'}</div>

      {/* Stars */}
      <div style={{ display: 'flex', gap: 2 }} onMouseLeave={() => setHover(0)}>
        {[1,2,3,4,5,6,7,8,9,10].map(s => (
          <span
            key={s}
            onClick={() => vote(s)}
            onMouseEnter={() => !isOwn && user && setHover(s)}
            style={{
              fontSize: 15, lineHeight: 1,
              color: s <= (hover || myScore || Math.round(score)) ? '#f0c040' : 'rgba(255,255,255,0.1)',
              cursor: !isOwn && user ? 'pointer' : 'default',
              transition: 'color .1s, transform .1s',
              transform: hover === s ? 'scale(1.3)' : 'scale(1)',
            }}
          >★</span>
        ))}
      </div>

      {/* Label */}
      <div style={{ fontSize: 10, color: 'rgba(200,180,255,0.4)', letterSpacing: 1, textTransform: 'uppercase', textAlign: 'center' }}>
        {isOwn ? 'Tavo reitingas' : myScore ? `Tavo balsas: ${myScore}/10` : user ? 'Įvertink žaidėją' : 'Reitingas'}
      </div>

      {/* Contact button */}
      {!isOwn && user && (
        <button
          onClick={onContact}
          style={{
            marginTop: 4, width: '100%',
            background: 'linear-gradient(135deg,#5a3e8c,#7b5cbf)',
            border: 'none', borderRadius: 8, color: '#fff',
            padding: '8px 14px', cursor: 'pointer', fontSize: 13, fontWeight: 700,
          }}
        >✉️ Susisiekti</button>
      )}
    </div>
  );
}

// ── Player Detail Sidebar ──────────────────────────────────────────────────────
function PlayerSidebar({ playerId, navigate }) {
  const [clubs, setClubs]         = useState([]);
  const [allEvents, setAllEvents] = useState([]);
  const [sideEvents, setSideEvents] = useState([]);
  const [news, setNews]           = useState([]);
  const [players, setPlayers]     = useState([]);

  useEffect(() => {
    api.get('/clubs?status=approved').then(r => {
      const shuffled = [...(r.data || [])].sort(() => Math.random() - 0.5);
      setClubs(shuffled.slice(0, 5));
    }).catch(() => {});

    api.get('/events').then(r => {
      const evs = r.data || [];
      setAllEvents(evs); // all events → calendar
      const twoWeeksAgo = new Date(Date.now() - 14 * 86400000).toISOString().split('T')[0];
      setSideEvents(evs.filter(e => e.date >= twoWeeksAgo).slice(0, 3));
    }).catch(() => {});

    api.get('/news').then(r => {
      setNews((r.data || []).slice(0, 4));
    }).catch(() => {});

    api.get(`/players/random?count=5&exclude=${playerId}`).then(r => {
      setPlayers(r.data || []);
    }).catch(() => {});
  }, [playerId]);

  return (
    <div className="pl-sidebar">
      {/* Calendar with events */}
      <div className="pl-sidebar-card" style={{ overflow: 'visible', position: 'relative' }}>
        <div className="pl-sidebar-title">📅 Kalendorius</div>
        <MiniCalendar events={allEvents} navigate={navigate} />
      </div>

      {/* Clubs */}
      {clubs.length > 0 && (
        <div className="pl-sidebar-card">
          <div className="pl-sidebar-title">🛡️ Klubai</div>
          {clubs.map(c => (
            <div key={c.id} className="pl-sidebar-item" onClick={() => navigate(`/clubs/${c.id}`)}>
              <span className="pl-sidebar-emoji">{c.emoji || '🐉'}</span>
              <div style={{ minWidth: 0 }}>
                <div className="pl-sidebar-name">{c.name}</div>
                {c.city && <div className="pl-sidebar-sub">📍 {c.city}</div>}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Recent events */}
      {sideEvents.length > 0 && (
        <div className="pl-sidebar-card">
          <div className="pl-sidebar-title">⚔️ Artimiausi renginiai</div>
          {sideEvents.map(e => (
            <div key={e.id} className="pl-sidebar-item" onClick={() => navigate(`/events/${e.id}`)}>
              <span className="pl-sidebar-emoji">{e.type === 'larp' ? '🗡️' : '🎲'}</span>
              <div style={{ minWidth: 0 }}>
                <div className="pl-sidebar-name">{e.name}</div>
                <div className="pl-sidebar-sub">{e.date}{e.city ? ` · ${e.city}` : ''}</div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Latest news */}
      {news.length > 0 && (
        <div className="pl-sidebar-card">
          <div className="pl-sidebar-title">📰 Naujienos</div>
          {news.map(n => (
            <div key={n.id} className="pl-sidebar-item" onClick={() => navigate(`/news/${n.id}`)}>
              <span className="pl-sidebar-emoji">📄</span>
              <div style={{ minWidth: 0 }}>
                <div className="pl-sidebar-name">{n.title}</div>
                <div className="pl-sidebar-sub">{n.category}</div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Random players */}
      {players.length > 0 && (
        <div className="pl-sidebar-card">
          <div className="pl-sidebar-title">🎲 Žaidėjai</div>
          {players.map(p => (
            <div key={p.user_id} className="pl-sidebar-item" onClick={() => navigate(`/players/${p.user_id}`)}>
              <Avatar player={p} size={28} />
              <div style={{ minWidth: 0 }}>
                <div className="pl-sidebar-name">{p.name}</div>
                <div className="pl-sidebar-sub">{ROLE_ICON[p.role]} {p.city || ''}</div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Badge nomination modal ────────────────────────────────────────────────────
function NominateModal({ playerId, badgeDefs, onClose, onDone }) {
  const [badgeId, setBadgeId] = useState('');
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  const submit = async () => {
    if (!badgeId) { setErr('Pasirinkite ženkliuką'); return; }
    if (!reason.trim()) { setErr('Parašykite priežastį'); return; }
    setSaving(true); setErr('');
    try {
      await api.post(`/players/${playerId}/badge-nominations`, { badge_id: parseInt(badgeId), reason: reason.trim() });
      onDone();
    } catch (e) {
      setErr(e.response?.data?.detail || 'Klaida');
    }
    setSaving(false);
  };

  const selectedDef = badgeDefs.find(x => String(x.id) === badgeId);

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', zIndex: 400, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      onClick={onClose}>
      <div style={{ background: 'var(--bg)', border: '1px solid var(--border2)', borderRadius: 12, padding: 28, maxWidth: 440, width: '90%' }}
        onClick={e => e.stopPropagation()}>
        <h3 style={{ fontFamily: 'Cinzel,serif', color: 'var(--gold2)', marginBottom: 18, fontSize: 16 }}>🏅 Nominuoti ženkliukui</h3>
        <div style={{ fontFamily: 'Cinzel,serif', fontSize: 10, color: 'var(--text3)', marginBottom: 8, letterSpacing: 1 }}>PASIRINKITE ŽENKLIUKĄ</div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 14, maxHeight: 200, overflowY: 'auto', padding: 4 }}>
          {badgeDefs.map(b => (
            <div key={b.id} onClick={() => setBadgeId(String(b.id))} title={b.name || `#${b.badge_num}`}
              style={{ cursor: 'pointer', border: badgeId === String(b.id) ? '2px solid var(--gold2)' : '2px solid transparent', borderRadius: 8, padding: 4, background: badgeId === String(b.id) ? 'rgba(240,192,64,0.1)' : 'transparent', transition: 'all .15s' }}>
              <img src={BADGE_IMGS[b.badge_num]} alt={b.name || b.badge_num} style={{ width: 44, height: 44, objectFit: 'contain', display: 'block' }} />
            </div>
          ))}
        </div>
        {selectedDef && (
          <div style={{ fontSize: 12, color: 'var(--gold2)', marginBottom: 12, fontWeight: 600 }}>
            {selectedDef.name || `Ženkliukas #${selectedDef.badge_num}`}{selectedDef.description ? ` — ${selectedDef.description}` : ''}
          </div>
        )}
        <div style={{ fontFamily: 'Cinzel,serif', fontSize: 10, color: 'var(--text3)', marginBottom: 6, letterSpacing: 1 }}>KODĖL NUSIPELNĖ?</div>
        <textarea value={reason} onChange={e => setReason(e.target.value)} rows={3}
          placeholder="Paaiškinkite, kodėl šis žaidėjas nusipelnė šio ženkliuko..."
          style={{ width: '100%', background: 'var(--bg2)', border: '1px solid var(--border2)', color: 'var(--text)', borderRadius: 8, padding: '8px 10px', fontSize: 13, resize: 'vertical', boxSizing: 'border-box' }} />
        {err && <div style={{ fontSize: 11, color: '#ff6060', marginTop: 6 }}>{err}</div>}
        <div style={{ display: 'flex', gap: 10, marginTop: 16, justifyContent: 'flex-end' }}>
          <button className="btn btn-outline btn-sm" onClick={onClose}>Atšaukti</button>
          <button className="btn btn-sm" onClick={submit} disabled={saving}>{saving ? 'Siunčiama...' : 'Pateikti nominaciją'}</button>
        </div>
      </div>
    </div>
  );
}

// ── Badges section ────────────────────────────────────────────────────────────
function BadgesSection({ playerId, viewerUserId, isOwnProfile }) {
  const [badges, setBadges] = useState([]);
  const [nominations, setNominations] = useState([]);
  const [allBadgeDefs, setAllBadgeDefs] = useState([]);
  const [showNominate, setShowNominate] = useState(false);
  const [selectedBadge, setSelectedBadge] = useState(null);
  const [voting, setVoting] = useState(null);
  const [votingErr, setVotingErr] = useState('');

  const reload = useCallback(() => {
    api.get(`/players/${playerId}/badges`).then(r => setBadges(r.data || [])).catch(() => {});
    api.get(`/players/${playerId}/badge-nominations`).then(r => setNominations(r.data || [])).catch(() => {});
  }, [playerId]);

  useEffect(() => {
    reload();
    api.get('/badges').then(r => setAllBadgeDefs(r.data || [])).catch(() => {});
  }, [reload]);

  const vote = async (nomId) => {
    setVoting(nomId); setVotingErr('');
    try {
      await api.post(`/badge-nominations/${nomId}/vote`);
      reload();
    } catch (e) {
      setVotingErr(e.response?.data?.detail || 'Klaida');
    }
    setVoting(null);
  };

  const pending = nominations.filter(n => n.status === 'pending');
  const approved = nominations.filter(n => n.status === 'approved');

  return (
    <div style={{ marginBottom: 24, background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 10, padding: '16px 20px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
        <div style={{ fontFamily: 'Cinzel,serif', fontSize: 12, fontWeight: 600, color: 'var(--gold2)', letterSpacing: 2, textTransform: 'uppercase' }}>🏅 Ženkliukai</div>
        {viewerUserId && !isOwnProfile && (
          <button className="btn btn-sm btn-outline" onClick={() => setShowNominate(true)} style={{ fontSize: 11 }}>+ Nominuoti</button>
        )}
      </div>

      {/* Awarded badges */}
      {badges.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginBottom: 16 }}>
          {badges.map(b => (
            <div key={b.id} onClick={() => setSelectedBadge(b)} title={b.name || `Ženkliukas #${b.badge_num}`}
              style={{ cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, width: 64 }}>
              <img src={BADGE_IMGS[b.badge_num]} alt={b.name || b.badge_num}
                style={{ width: 52, height: 52, objectFit: 'contain', filter: 'drop-shadow(0 2px 6px rgba(240,192,64,0.4))' }} />
              <div style={{ fontSize: 9, color: 'var(--gold2)', textAlign: 'center', lineHeight: 1.2, maxWidth: 60, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {b.name || `#${b.badge_num}`}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Approved nominations — open for voting */}
      {approved.length > 0 && (
        <div style={{ marginBottom: pending.length ? 14 : 0 }}>
          <div style={{ fontSize: 10, color: 'rgba(100,200,100,0.7)', fontFamily: 'Cinzel,serif', letterSpacing: 2, textTransform: 'uppercase', marginBottom: 8 }}>🗳️ Balsuojama</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {approved.map(n => (
              <div key={n.id} style={{ background: 'var(--bg)', border: '1px solid rgba(100,200,100,0.2)', borderRadius: 7, padding: '8px 12px', display: 'flex', alignItems: 'center', gap: 10 }}>
                <img src={BADGE_IMGS[n.badge_num]} alt={n.badge_name || n.badge_num} style={{ width: 36, height: 36, objectFit: 'contain', flexShrink: 0 }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text)' }}>{n.badge_name || `Ženkliukas #${n.badge_num}`}</div>
                  <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 2 }}>{n.reason}</div>
                  {n.nominator_name && <div style={{ fontSize: 10, color: 'var(--text3)', marginTop: 2 }}>Nominavo: {n.nominator_name}</div>}
                </div>
                <div style={{ textAlign: 'center', flexShrink: 0 }}>
                  <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--gold2)', marginBottom: 4 }}>{n.vote_count}<span style={{ fontSize: 10, color: 'var(--text3)', fontWeight: 400 }}>/10</span></div>
                  {viewerUserId && !isOwnProfile && (
                    <button className="btn btn-sm" style={{ fontSize: 10, padding: '3px 8px' }}
                      onClick={() => vote(n.id)} disabled={voting === n.id}>
                      {voting === n.id ? '...' : '👍 Balsuoti'}
                    </button>
                  )}
                  {(!viewerUserId) && <div style={{ fontSize: 10, color: 'var(--text3)' }}>Prisijunkite balsuoti</div>}
                </div>
              </div>
            ))}
          </div>
          {votingErr && <div style={{ fontSize: 11, color: '#ff6060', marginTop: 6 }}>{votingErr}</div>}
        </div>
      )}

      {/* Pending nominations — waiting for admin */}
      {pending.length > 0 && (
        <div>
          <div style={{ fontSize: 10, color: 'rgba(255,180,50,0.7)', fontFamily: 'Cinzel,serif', letterSpacing: 2, textTransform: 'uppercase', marginBottom: 8 }}>⏳ Laukia patvirtinimo</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {pending.map(n => (
              <div key={n.id} style={{ background: 'var(--bg)', border: '1px solid rgba(255,180,50,0.15)', borderRadius: 7, padding: '8px 12px', display: 'flex', alignItems: 'center', gap: 10, opacity: 0.75 }}>
                <img src={BADGE_IMGS[n.badge_num]} alt={n.badge_name || n.badge_num} style={{ width: 32, height: 32, objectFit: 'contain', flexShrink: 0, filter: 'grayscale(0.4)' }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text)' }}>{n.badge_name || `Ženkliukas #${n.badge_num}`}</div>
                  <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 1 }}>{n.reason}</div>
                  {n.nominator_name && <div style={{ fontSize: 10, color: 'var(--text3)', marginTop: 1 }}>Nominavo: {n.nominator_name}</div>}
                </div>
                <span style={{ fontSize: 10, color: 'rgba(255,180,50,0.7)', background: 'rgba(255,180,50,0.1)', border: '1px solid rgba(255,180,50,0.2)', borderRadius: 4, padding: '2px 7px', flexShrink: 0 }}>Laukia</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Empty state */}
      {badges.length === 0 && nominations.length === 0 && (
        <div style={{ fontSize: 12, color: 'var(--text3)', textAlign: 'center', padding: '12px 0' }}>
          {isOwnProfile ? 'Ženkliukų dar nėra. Kiti žaidėjai gali tave nominuoti.' : 'Ženkliukų dar nėra'}
        </div>
      )}

      {/* Nominate modal */}
      {showNominate && (
        <NominateModal
          playerId={playerId}
          badgeDefs={allBadgeDefs}
          onClose={() => setShowNominate(false)}
          onDone={() => { setShowNominate(false); reload(); }}
        />
      )}

      {/* Badge detail popup */}
      {selectedBadge && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', zIndex: 400, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          onClick={() => setSelectedBadge(null)}>
          <div style={{ background: 'var(--bg)', border: '1px solid var(--border2)', borderRadius: 12, padding: 28, maxWidth: 360, width: '90%', textAlign: 'center' }}
            onClick={e => e.stopPropagation()}>
            <img src={BADGE_IMGS[selectedBadge.badge_num]} alt={selectedBadge.name || selectedBadge.badge_num}
              style={{ width: 80, height: 80, objectFit: 'contain', marginBottom: 14 }} />
            <div style={{ fontFamily: 'Cinzel,serif', fontSize: 16, fontWeight: 700, color: 'var(--gold2)', marginBottom: 8 }}>
              {selectedBadge.name || `Ženkliukas #${selectedBadge.badge_num}`}
            </div>
            {selectedBadge.description && <div style={{ fontSize: 13, color: 'var(--text)', marginBottom: 10 }}>{selectedBadge.description}</div>}
            {selectedBadge.reason && <div style={{ fontSize: 12, color: 'var(--text3)', marginBottom: 8, fontStyle: 'italic' }}>"{selectedBadge.reason}"</div>}
            {selectedBadge.nominator_name && <div style={{ fontSize: 11, color: 'var(--text3)' }}>Nominavo: {selectedBadge.nominator_name}</div>}
            <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 4 }}>Gauta: {(selectedBadge.awarded_at || '').slice(0, 10)}</div>
            <button className="btn btn-outline btn-sm" style={{ marginTop: 16 }} onClick={() => setSelectedBadge(null)}>Uždaryti</button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Player Detail Page ────────────────────────────────────────────────────────
export function PlayerDetailPage() {
  const { id } = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [player, setPlayer] = useState(null);
  const [memberships, setMemberships] = useState([]);
  const [notFound, setNotFound] = useState(false);
  const [contactOpen, setContactOpen] = useState(false);

  useEffect(() => {
    api.get(`/players/${id}`)
      .then(r => setPlayer(_parsePlayer(r.data)))
      .catch(() => setNotFound(true));
    api.get(`/players/${id}/memberships`)
      .then(r => setMemberships(r.data || []))
      .catch(() => {});
  }, [id]); // eslint-disable-line

  if (notFound) return (
    <div style={{ maxWidth: 640, margin: '60px auto', textAlign: 'center', padding: '0 20px' }}>
      <div style={{ fontSize: 56, marginBottom: 16 }}>🧙</div>
      <h2 style={{ fontFamily: 'Cinzel,serif', color: '#f0c040', marginBottom: 12 }}>Žaidėjas nerastas</h2>
      <button className="btn btn-outline" onClick={() => navigate('/players')}>← {t('common.back')}</button>
    </div>
  );

  if (!player) return (
    <div style={{ padding: 60, textAlign: 'center', color: 'var(--text3)' }}>{t('common.loading')}</div>
  );

  const joined = player.joined
    ? new Date(player.joined).toLocaleDateString('lt-LT', { year: 'numeric', month: 'long' })
    : null;

  return (
    <div className="pl-detail">
      <button className="btn btn-secondary btn-sm" style={{ marginBottom: 20 }} onClick={() => navigate(-1)}>
        ← {t('common.back')}
      </button>

      {/* Hero – full width above the 2-col layout */}
      <div className="pl-detail-hero">
        {player.header_image && <img src={player.header_image} alt="" className="pl-detail-hero-img" />}
        <div className="pl-detail-hero-inner">
          <Avatar player={player} size={96} />
          <div style={{ flex: 1, minWidth: 200 }}>
            <h1 className="pl-detail-name">{player.name}</h1>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 10 }}>
              <RoleBadge role={player.role} t={t} />
              <ExpBadge exp={player.experience} t={t} />
            </div>
            <div className="pl-detail-meta">
              {(player.city || player.country) && (
                <span>
                  {player.country && COUNTRY_FLAG[player.country] && <span style={{ marginRight: 4 }}>{COUNTRY_FLAG[player.country]}</span>}
                  {player.city && <>📍 {player.city}</>}
                </span>
              )}
              {joined && <span>📅 {t('players.join_date')}: {joined}</span>}
            </div>
          </div>
          {/* Rating + Contact — right column of hero */}
          <RatingWidget
            playerId={player.user_id}
            user={user}
            isOwn={user && user.id === player.user_id}
            onContact={() => setContactOpen(true)}
          />
        </div>
      </div>

      {/* 2-column layout: main content + sidebar */}
      <div className="pl-detail-layout">
        {/* Left: profile sections */}
        <div className="pl-detail-sections">
          {/* Badges */}
          <BadgesSection
            playerId={parseInt(id)}
            viewerUserId={user?.id}
            isOwnProfile={user && user.id === player.user_id}
          />

          {player.bio && (
            <div className="pl-detail-bio-card">
              <div className="pl-section-lbl">{t('players.bio_label')}</div>
              <p className="pl-detail-bio-text">{player.bio}</p>
            </div>
          )}

          <div className="pl-detail-grid">

            {/* Vaidmuo */}
            <InfoCard label={t('players.filter_role')}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {['player', 'gm', 'both'].map(r => (
                  <span key={r} className="pl-detail-row" style={{ color: ROLE_COLOR[r] || '#888' }}>
                    <span className="pl-detail-check" style={{ color: '#f0c040' }}>✓</span>
                    {ROLE_ICON[r]} {t(`players.role_${r}`)}
                  </span>
                )).filter((_, i) => ['player', 'gm', 'both'][i] === player.role)}
              </div>
            </InfoCard>

            {/* Patirtis */}
            <InfoCard label={t('players.filter_exp')}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                <span className="pl-detail-row" style={{ color: EXP_COLOR[player.experience] || '#888', fontWeight: 700 }}>
                  <span className="pl-detail-check" style={{ color: '#f0c040' }}>✓</span>
                  {t(`players.exp_${player.experience}`)}
                </span>
              </div>
            </InfoCard>

            {/* Kalbos */}
            {(player.languages || []).length > 0 && (
              <InfoCard label={t('players.languages_label')}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {LANG_OPTS.filter(l => (player.languages || []).includes(l)).map(l => (
                    <span key={l} className="pl-detail-row" style={{ color: '#80a8ff', fontWeight: 700 }}>
                      <span className="pl-detail-check" style={{ color: '#f0c040' }}>✓</span>
                      {l.toUpperCase()}
                    </span>
                  ))}
                </div>
              </InfoCard>
            )}

            {/* Žaidžia */}
            {(player.systems || []).length > 0 && (
              <InfoCard label={t('players.systems_label')} wide>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(200px,1fr))', gap: '4px 16px' }}>
                  {[
                    ...POPULAR_SYSTEMS.filter(s => (player.systems || []).includes(s)),
                    ...(player.systems || []).filter(s => !POPULAR_SYSTEMS.includes(s)),
                  ].map((s, i) => (
                    <span key={i} className="pl-detail-row" style={{ color: '#c8b4ff', fontWeight: 700 }}>
                      <span className="pl-detail-check" style={{ color: '#f0c040' }}>✓</span>
                      {s}
                    </span>
                  ))}
                </div>
              </InfoCard>
            )}

            {/* Laikas */}
            {(player.availability || []).length > 0 && (
              <InfoCard label={t('players.availability_label')}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {AVAIL_OPTS.filter(a => (player.availability || []).includes(a)).map(a => (
                    <span key={a} className="pl-detail-row" style={{ color: '#6ddb8a', fontWeight: 700 }}>
                      <span className="pl-detail-check" style={{ color: '#f0c040' }}>✓</span>
                      {t(`players.avail_${a}`)}
                    </span>
                  ))}
                </div>
              </InfoCard>
            )}

            {/* Ieško */}
            {(player.looking_for || []).length > 0 && (
              <InfoCard label={t('players.looking_for_label')}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {LOOK_OPTS.filter(l => (player.looking_for || []).includes(l)).map(l => (
                    <span key={l} className="pl-detail-row" style={{ color: '#f0c040', fontWeight: 700 }}>
                      <span className="pl-detail-check" style={{ color: '#f0c040' }}>✓</span>
                      {t(`players.looking_${l}`)}
                    </span>
                  ))}
                </div>
              </InfoCard>
            )}

            {/* Kontaktai */}
            {(player.discord || player.website) && (
              <InfoCard label={t('players.contact_label')}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {player.discord && <span style={{ fontSize: 13, color: 'rgba(200,180,255,0.85)', display: 'flex', alignItems: 'center', gap: 6 }}>💬 {player.discord}</span>}
                  {player.website && <a href={player.website} target="_blank" rel="noreferrer" style={{ fontSize: 13, color: '#f0c040', textDecoration: 'none' }}>🔗 {player.website.replace(/^https?:\/\//, '').split('/')[0]}</a>}
                </div>
              </InfoCard>
            )}

            {/* Klubai */}
            {memberships.length > 0 && (
              <InfoCard label={t('players.memberships_label')}>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  {memberships.map(c => (
                    <span key={c.id} onClick={() => navigate(`/clubs/${c.id}`)} className="pl-tag pl-tag-look" style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12, padding: '4px 10px', borderRadius: 6, cursor: 'pointer' }}>
                      {c.emoji && <span>{c.emoji}</span>}{c.name}
                    </span>
                  ))}
                </div>
              </InfoCard>
            )}
          </div>
        </div>

        {/* Right: sidebar */}
        <PlayerSidebar playerId={player.user_id} navigate={navigate} />
      </div>

      {contactOpen && <ContactModal player={player} onClose={() => setContactOpen(false)} user={user} t={t} />}
    </div>
  );
}

function _parsePlayer(d) {
  for (const k of ['languages', 'systems', 'availability', 'looking_for']) {
    if (Array.isArray(d[k])) continue;
    try { d[k] = JSON.parse(d[k] || '[]'); } catch { d[k] = []; }
  }
  return d;
}
